import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import C from '../../colors';
import {
  getSelectionStatus,
  orderWorkflowStep,
  paymentStatusMeta,
  selectionStatusMeta,
} from '../../orderStatus';

const STEPS = [
  { id: 'step1', num: '01', title: '选片设置', hint: 'Selection' },
  { id: 'step2', num: '02', title: '照片配置', hint: 'Settings' },
  { id: 'step3', num: '03', title: '照片管理', hint: 'Library' },
  { id: 'step4', num: '04', title: '分享交付', hint: 'Share' },
  { id: 'step5', num: '05', title: '选图记录', hint: 'Records' },
];

function Icon({ name, size = 14 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (name === 'back') return <svg viewBox="0 0 24 24" {...s}><path d="M19 12H5M11 18l-6-6 6-6"/></svg>;
  if (name === 'check') return <svg viewBox="0 0 24 24" {...s} strokeWidth="2"><path d="m5 13 4 4L19 7"/></svg>;
  return null;
}

function PrimaryBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 14, fontWeight: 500,
      padding: '10px 18px', borderRadius: 7,
      background: C.brand, color: '#fff', border: 'none',
      cursor: 'pointer',
      boxShadow: `0 1px 0 ${C.brand2}, 0 6px 16px -8px rgba(217,79,146,.55)`,
      whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

function GhostBtn({ children, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: C.sans, fontSize: 13, fontWeight: 500,
      padding: '9px 14px', borderRadius: 7,
      background: '#fff', color: C.ink,
      border: `1px solid ${C.hair2}`,
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

export default function StepShell({ order, children }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const currentStep = STEPS.find(s => path.includes(s.id))?.id || 'step1';
  const stepIdx = STEPS.findIndex(s => s.id === currentStep);
  const workflowStep = orderWorkflowStep(order);
  const selectionMeta = selectionStatusMeta(order);
  const paymentMeta = paymentStatusMeta(order);
  const selectionStatus = getSelectionStatus(order);
  const paymentProgress = order?.paymentSummary?.payableCount > 0
    ? ` ${order.paymentSummary.paidCount}/${order.paymentSummary.payableCount}`
    : '';

  // Which steps are actually completed (regardless of current position)
  const stepDone = {
    step1: true,
    step2: !!order?.id,
    step3: (order?.Album?.pictureCount || 0) > 0,
    step4: !!order?.Album?.password,
    step5: (order?.selectionNum || 0) > 0 || selectionStatus === 'submitted' || selectionStatus === 'expired',
  };

  const goStep = (stepId) => {
    navigate(`/user/orders/${orderId}/${stepId}`);
  };

  const orderLabel = (order?.customerName || '未知') + (order?.shootLocation ? ` · ${order.shootLocation}` : '');
  const packName = order?.package?.name || '';
  const createdAt = order?.createdAt || order?.shootTime;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>

      {/* ── Order context header ── */}
      <div style={{
        padding: '20px 36px 0',
        background: C.paper,
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(700px 180px at 100% -50%, ${C.brand}24, transparent 70%)`,
        }}/>
        <div style={{ position: 'relative' }}>
          <button onClick={() => navigate('/user/orders')} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginBottom: 10, padding: '5px 10px 5px 6px',
            background: 'none', border: `1px solid ${C.hair}`,
            borderRadius: 7, cursor: 'pointer',
            fontFamily: C.sans, fontSize: 12, color: C.ink3,
            transition: 'color .15s, border-color .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = C.ink; e.currentTarget.style.borderColor = C.hair2; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.ink3; e.currentTarget.style.borderColor = C.hair; }}
          >
            <Icon name="back" size={13}/>
            订单列表
          </button>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
            fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5,
            color: C.ink3, textTransform: 'uppercase', fontWeight: 600,
            flexWrap: 'wrap',
          }}>
            <span>{packName}</span>
            {createdAt && <>
              <span style={{ color: C.hair2 }}>·</span>
              <span>创建于 {new Date(createdAt).toLocaleDateString('zh-CN')}</span>
            </>}
            <span data-testid="step-selection-status" data-status={selectionMeta.status} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '2px 8px', borderRadius: 10,
              background: C.brandSoft, color: '#B7406E',
              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
              marginLeft: 4,
            }}>
              <span style={{ width: 5, height: 5, borderRadius: 3, background: C.brand }}/>
              选片：{selectionMeta.label}
            </span>
            <span data-testid="step-payment-status" data-status={paymentMeta.status} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '2px 8px', borderRadius: 10,
              background: C.neutralBg, color: C.neutralFg,
              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
            }}>支付：{paymentMeta.label}{paymentProgress}</span>
            <span data-testid="order-current-workflow-step" data-step={workflowStep.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '2px 8px', borderRadius: 10,
              background: C.paper2, color: C.ink2,
              fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
            }}>当前阶段：{workflowStep.label}</span>
          </div>
          <h2 style={{
            margin: 0,
            fontFamily: C.serif, fontSize: 32, lineHeight: 1.05,
            fontWeight: 400, color: C.ink, letterSpacing: -0.5,
          }}>
            {orderLabel}
          </h2>
        </div>
      </div>

      {/* ── Horizontal Stepper ── */}
      <div style={{
        padding: '20px 36px 0',
        background: C.paper,
        borderBottom: `1px solid ${C.hair}`,
        flexShrink: 0,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`,
          gap: 0,
          position: 'relative',
        }}>
          {STEPS.map((s, i) => {
            const active = s.id === currentStep;
            const done = stepDone[s.id];
            return (
              <button
                key={s.id}
                data-testid={`order-step-${s.id}`}
                data-active={active ? 'true' : 'false'}
                data-complete={done ? 'true' : 'false'}
                onClick={() => goStep(s.id)}
                style={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: '34px 1fr',
                  gap: 12, alignItems: 'center',
                  padding: '12px 14px 16px 0',
                  border: 'none', background: 'transparent',
                  cursor: 'pointer', textAlign: 'left',
                  fontFamily: C.sans, color: 'inherit',
                }}
              >
                {/* circle */}
                <div style={{
                  width: 34, height: 34, borderRadius: 17,
                  display: 'grid', placeItems: 'center',
                  background: active ? C.brand : done ? C.ink : '#fff',
                  border: active || done ? 'none' : `1px solid ${C.hair2}`,
                  color: active || done ? '#fff' : C.ink3,
                  fontFamily: C.serif, fontSize: 15,
                  boxShadow: active ? '0 4px 14px -6px rgba(217,79,146,.55)' : 'none',
                  transition: 'all .2s',
                }}>
                  {done ? <Icon name="check" size={14}/> : s.num}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: C.mono, fontSize: 10, letterSpacing: 1.3,
                    textTransform: 'uppercase',
                    color: active ? C.brand2 : done ? C.ink2 : C.ink3,
                    fontWeight: 600, marginBottom: 2,
                  }}>
                    Step {s.num} · {s.hint}
                  </div>
                  <div style={{
                    fontSize: 15, fontWeight: active ? 600 : 500,
                    color: active ? C.ink : done ? C.ink2 : C.ink3,
                    lineHeight: 1.15,
                  }}>
                    {s.title}
                  </div>
                </div>
                {/* underline accent for active */}
                <div style={{
                  position: 'absolute', left: 0, right: 16, bottom: 0,
                  height: 2, borderRadius: 1,
                  background: active ? C.brand : 'transparent',
                  transition: 'background .2s',
                }}/>
                {/* connector line into next step */}
                {i < STEPS.length - 1 && (
                  <div style={{
                    position: 'absolute', top: 28, left: 46, right: -2,
                    height: 1,
                    background: done ? C.ink2 : C.hair,
                    zIndex: -1,
                  }}/>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Step content ── */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>

      {/* ── Footer nav ── */}
      <footer style={{
        borderTop: `1px solid ${C.hair}`,
        padding: '14px 36px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: C.paper,
        flexShrink: 0,
      }}>
        <div style={{ fontFamily: C.mono, fontSize: 11, color: C.ink3, letterSpacing: 0.8, textTransform: 'uppercase' }}>
          Step {String(stepIdx + 1).padStart(2, '0')} of {String(STEPS.length).padStart(2, '0')}
          <span style={{ margin: '0 8px', color: C.hair2 }}>·</span>
          <span style={{ color: C.ink2, textTransform: 'none', letterSpacing: 0, fontFamily: C.sans, fontSize: 13 }}>
            {STEPS[stepIdx]?.title}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {stepIdx > 0 && (
            <GhostBtn onClick={() => goStep(STEPS[stepIdx - 1].id)}>
              ← {STEPS[stepIdx - 1].title}
            </GhostBtn>
          )}
          {stepIdx < STEPS.length - 1 && (
            <PrimaryBtn onClick={() => goStep(STEPS[stepIdx + 1].id)}>
              {STEPS[stepIdx + 1].title} →
            </PrimaryBtn>
          )}
        </div>
      </footer>
    </div>
  );
}

export { STEPS };
